"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import CheckboxGroup from "@/components/custom/checkbox-group";
import RefreshIcon from "@/components/custom/icons/refresh";
import SearchIcon from "@/components/custom/icons/search";
import { Typography } from "@/components/custom/typography";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCommonCDByCodes } from "@/hooks/useCommonCD";
import { AgeGroup, Gender } from "@/services/graphql/generated";
import { useMapStore } from "@/stores/mapStore";
import { useMapUIStore } from "@/stores/mapUIStore";
import { useReSearchStore } from "@/stores/reSearchStore";
import { useSearchConditionStore } from "@/stores/searchConditionStore";
import { useUser } from "@/stores/user";
import { CommonCDType } from "@/types/common-cd";
import { useMap } from "@vis.gl/react-google-maps";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import ProductCategoryNoteList from "./ProductCategoryNoteList";
import { SearchAdOptions } from "./SearchAdOptions";
import { useTypedTranslations } from "@/i18n/typed";
import { useGuestSearchLimit } from "@/hooks/useGuestSearchLimit";
import Link from "next/link";
import { cn, isEmptyObject } from "@/lib/utils";
import { environments } from "@/lib/config";

/* -------------------- SCHEMA -------------------- */
const formSchema = z.object({
  category: z.string(),
  target: z.string(),
  purpose: z.string(),
  budget: z.string().min(1),
  gender: z.string().min(1),
  ageGroups: z.array(z.string()).min(1),
  footsprint_traffic: z.string().optional(),
  generalAd: z.boolean().optional().readonly(),
});

type FormValues = z.infer<typeof formSchema>;

function onceIdle(map: google.maps.Map) {
  return new Promise<void>((resolve) => {
    const listener = map.addListener("idle", () => {
      listener.remove();
      resolve();
    });
  });
}

export default function AdSearchForm() {
  const router = useRouter();
  const t = useTypedTranslations("search");
  const searchParams = useSearchParams();

  const { data: productCategories } = useCommonCDByCodes([
    "ad_target_category",
    "ad_objective",
    "ad_type",
  ]);

  const map = useMap();
  const { userInfo } = useUser();

  const { setCondition } = useSearchConditionStore();
  const {
    isResetMapSearchForm,
    setResetMapSearchForm,
    setIsResearch,
    setHasSearched,
    resetSearchState,
  } = useReSearchStore();
  const { setBounds } = useMapStore();
  const { openClusterList } = useMapUIStore();

  const [adTypes, setAdTypes] = useState<string[]>([]);
  const {
    remainingCount,
    isLimitReached,
    consumeSearch,
  } = useGuestSearchLimit();
  const isGuest = isEmptyObject(userInfo?.user);

  const params = Object.fromEntries(searchParams.entries());
  const isAllGender = params?.gender
    ?.split(",")
    .every((g) => Object.values(Gender).includes(g as Gender));

  /* -------------------- I18N OPTIONS -------------------- */
  const ageOptions = useMemo(
    () =>
      [
        { label: t("ageGroup.10_14"), value: AgeGroup.R10_14 },
        { label: t("ageGroup.15_19"), value: AgeGroup.R15_19 },
        { label: t("ageGroup.20_24"), value: AgeGroup.R20_24 },
        { label: t("ageGroup.25_29"), value: AgeGroup.R25_29 },
        { label: t("ageGroup.30s"), value: AgeGroup.A30S },
        { label: t("ageGroup.40s"), value: AgeGroup.A40S },
        { label: t("ageGroup.50s"), value: AgeGroup.A50S },
        { label: t("ageGroup.70plus"), value: AgeGroup.A70S },
      ] as { label: string; value: AgeGroup }[],
    [t],
  );

  const genderOptions = useMemo(
    () =>
      [
        { label: t("gender.all"), value: "ALL" },
        { label: t("gender.male"), value: Gender.Male },
        { label: t("gender.female"), value: Gender.Female },
      ] as { label: string; value: Gender | "ALL" }[],
    [t],
  );

  const trafficOptions = useMemo(
    () => [
      { label: t("traffic.none"), value: "none" },
      { label: t("traffic.500"), value: "500" },
      { label: t("traffic.1000"), value: "1000" },
      { label: t("traffic.2000"), value: "2000" },
      { label: t("traffic.5000"), value: "5000" },
      { label: t("traffic.10000"), value: "10000" },
    ],
    [t],
  );

  /* -------------------- FORM -------------------- */
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      category: params.category ?? "",
      target: params.target ?? "",
      purpose: params.purpose ?? "",
      budget: params.b ?? "",
      gender: isAllGender ? "ALL" : (params.gender ?? ""),
      ageGroups: params?.age_group?.split(",") ?? [],
      footsprint_traffic: params.traffic ?? "",
    },
  });

  // Watch required fields for button activation
  const watchedBudget = form.watch("budget");
  const watchedGender = form.watch("gender");
  const watchedAgeGroups = form.watch("ageGroups");

  const isFormIncomplete =
    !watchedBudget ||
    !watchedGender ||
    !watchedAgeGroups ||
    watchedAgeGroups.length === 0;

  const isSearchDisabled = isFormIncomplete || (isGuest && isLimitReached);

  useEffect(() => {
    if (isResetMapSearchForm) {
      form.reset(form.formState.defaultValues);
      setResetMapSearchForm(false);
    }
  }, [isResetMapSearchForm, form, setResetMapSearchForm]);

  useEffect(() => {
    if (params.ad_type) {
      const adTypesFromParam = params.ad_type.split(",");
      setAdTypes(adTypesFromParam);
    }
  }, []);

  /* -------------------- SUBMIT HANDLER -------------------- */
  const onSubmit = async (data: FormValues) => {
    if (!map) {
      setIsResearch(false);
      return;
    }

    // Consume search count for guests
    if (isGuest) {
      const canSearch = await consumeSearch();
      if (!canSearch) {
        return;
      }
    }

    resetSearchState();
    setIsResearch(true);
    setHasSearched(true);

    useMapUIStore.getState().resetClusterState();
    setCondition({ mediaGroupIds: undefined, mediaId: undefined });

    const geocoder = new google.maps.Geocoder();
    const geoAddress = userInfo?.country || userInfo?.country_code;

    geocoder.geocode({ address: geoAddress }, async (results, status) => {
      if (status !== "OK" || !results?.[0]) {
        setIsResearch(false);
        return;
      }

      try {
        const viewport = results[0].geometry.viewport;

        map.fitBounds(viewport);
        await onceIdle(map);

        // Build query params
        const paramsObj: Record<string, string | undefined> = {
          // NOTE: server expects "b"
          b: data.budget || undefined,
          gender:
            data.gender === "ALL"
              ? [Gender.Male, Gender.Female].join(",")
              : data.gender,
          ad_type: adTypes.length ? adTypes.join(",") : undefined,
          age_group: data.ageGroups?.length
            ? data.ageGroups.join(",")
            : undefined,
          traffic:
            data.footsprint_traffic === "none"
              ? undefined
              : data.footsprint_traffic || undefined,
        };

        setCondition(paramsObj);

        const qs = new URLSearchParams();
        Object.entries(paramsObj).forEach(([k, v]) => v && qs.set(k, v));
        router.push(`/?${qs.toString()}`);

        setBounds(viewport);
        openClusterList();
      } finally {
        setIsResearch(false);
      }
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col mx-auto h-full"
      >
        <ScrollArea className="h-[calc(100vh-136px-var(--header-height))] flex-1">
          <div className="flex flex-col gap-4 p-2.5">
            {/* HEADER */}
            <div className="flex items-center mb-2">
              <Typography
                tone="strong"
                variant="body2"
                className="flex-1 font-semibold"
              >
                {t("advertisingInformation")}
              </Typography>

              <Button
                onClick={() => {
                  form.reset(form.formState.defaultValues);
                  setAdTypes([]);
                  resetSearchState();
                  router.push(`/`);
                }}
                size="sm"
                variant="secondary"
                tone="light"
                type="button"
                className="text-gray-700 px-2 py-1 gap-0.5 border border-gray-500 w-[123px] h-[30px]"
              >
                <RefreshIcon className="w-[14px] h-[12px]" />
                <Typography variant="body2">
                  {t("initializeInputValues")}
                </Typography>
              </Button>
            </div>

            {/* CATEGORY + TARGET */}
            <div className="grid grid-cols-2 items-start gap-2">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1 mb-1 font-normal">
                      <ProductCategoryNoteList />
                      {t("productCategory")}
                    </FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger className="p-3 font-medium">
                          <SelectValue
                            placeholder={t("productCategoryPlaceholder")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {productCategories?.data
                            ?.filter(
                              (item) => item.grp_code === "ad_target_category",
                            )
                            .map(
                              (item: CommonCDType) =>
                                item && (
                                  <SelectItem
                                    key={item.grp_sub_cd}
                                    value={`${item.grp_sub_cd}`}
                                  >
                                    {item.common_cd_name}
                                  </SelectItem>
                                ),
                            )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage keyT="validation" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="target"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="mb-1 font-normal">
                      {t("advertisingTarget")}
                    </FormLabel>
                    <FormControl className="font-medium">
                      <Input
                        placeholder={t("advertisingTargetPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage keyT="validation" />
                  </FormItem>
                )}
              />
            </div>

            {/* PURPOSE */}
            <FormField
              control={form.control}
              name="purpose"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="mb-1 font-normal">
                    {t("advertisingPurpose")}
                  </FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="p-3 font-medium">
                        <SelectValue
                          placeholder={t("advertisingPurposePlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {productCategories?.data
                          ?.filter((item) => item.grp_code === "ad_objective")
                          .map(
                            (item: CommonCDType) =>
                              item && (
                                <SelectItem
                                  key={item.grp_sub_cd}
                                  value={`${item.grp_sub_cd}`}
                                >
                                  {item.common_cd_name}
                                </SelectItem>
                              ),
                          )}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage keyT="validation" />
                </FormItem>
              )}
            />

            {/* BUDGET */}
            <FormField
              control={form.control}
              name="budget"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="mb-1 font-normal gap-0">
                    {t("monthlyBudget")}
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl className="font-medium">
                    <Input
                      className="mb-2"
                      type="number"
                      placeholder={t("monthlyBudgetPlaceholder")}
                      suffix={t("budgetUnit")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* TARGET AUDIENCE */}
            <div>
              <Typography
                tone="strong"
                variant="body2"
                className="flex-1 font-semibold mb-1"
              >
                {t("targetAudience")}
              </Typography>
              <Typography variant="detail1">
                {t("targetAudienceDesc")}
              </Typography>

              {/* GENDER */}
              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem className="flex w-full items-start space-x-4 mt-5">
                    <FormLabel className="mt-1">
                      <Typography variant="detail1" className="space-x-1">
                        {t("gender.label")}
                        <span className="text-destructive">*</span>
                      </Typography>
                    </FormLabel>

                    <FormControl className="flex-1">
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="gap-2 grid grid-cols-3"
                      >
                        {genderOptions.map((g) => (
                          <FormItem key={g.value} className="flex">
                            <Label
                              className="text-xs"
                              htmlFor={`gender-${g.value}`}
                            >
                              <FormControl>
                                <RadioGroupItem
                                  value={g.value}
                                  id={`gender-${g.value}`}
                                  className="border-2 border-secondary-800 shadow-none"
                                />
                              </FormControl>
                              <Typography
                                className="text-sm"
                                as="span"
                                tone="medium"
                              >
                                {g.label}
                              </Typography>
                            </Label>
                          </FormItem>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* AGE GROUPS */}
              <FormField
                control={form.control}
                name="ageGroups"
                render={({ field }) => (
                  <FormItem className="flex mt-4 space-x-4 items-start">
                    <FormLabel className="mt-1.5">
                      <Typography variant="detail1">
                        {t("age.label")}
                        <span className="text-destructive">*</span>
                      </Typography>
                    </FormLabel>

                    <div>
                      <CheckboxGroup
                        options={ageOptions}
                        className="w-full"
                        checkAllLabel={{ label: t("age.all"), inGroup: true }}
                        values={field.value || []}
                        onChange={(value) => field.onChange(value)}
                      />
                    </div>
                  </FormItem>
                )}
              />

              {/* TRAFFIC */}
              <FormField
                control={form.control}
                name="footsprint_traffic"
                render={({ field }) => (
                  <FormItem className="mt-3">
                    <FormLabel>
                      <Typography variant="detail1">
                        {t("dailyFootTraffic")}
                      </Typography>
                    </FormLabel>

                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="p-3 font-medium">
                          <SelectValue
                            placeholder={t("dailyFootTrafficPlaceholder")}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {trafficOptions.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage keyT="validation" />
                  </FormItem>
                )}
              />
            </div>

            {/* AD OPTIONS */}
            <div>
              <Typography
                tone="strong"
                variant="body2"
                className="flex-1 font-semibold mb-1"
              >
                {t("advertisingOptions")}
              </Typography>
              <Typography variant="detail1" className="mb-5 block">
                {t("advertisingOptionsDesc")}
              </Typography>

              <SearchAdOptions
                options={
                  productCategories?.data?.filter(
                    (item) => item.grp_code === "ad_type",
                  ) || []
                }
                value={adTypes}
                onChange={(data) => {
                  const adTypeValues = data.map((item) => item.grp_sub_cd);
                  setAdTypes(adTypeValues);
                }}
              />
            </div>
          </div>
        </ScrollArea>
        <div className="space-y-2 flex flex-col justify-center">
          {isFormIncomplete && (
            <Typography
              variant="detail1"
              className="text-destructive self-center"
              as="span"
            >
              {t("disabledSearchText.message")}
            </Typography>
          )}

          {/* GUEST SEARCH LIMIT BAR */}
          {isGuest && (
            <div className={cn("mx-3 p-1 rounded-md flex justify-between shrink-0", isLimitReached ? "bg-red-500" : "bg-red-50",)}>
              <Typography variant="detail1" as="span" className="text-white font-medium">
                {t("guestSearchLimit.remainingSearches", { count: remainingCount })}
              </Typography>
              <Typography variant="detail1" as="span" className="text-white font-medium">
                <Link href={environments.authServerUrl ?? ""} className="underline underline-offset-2 mr-1">
                  {t("guestSearchLimit.loginForMore")}
                </Link>
                {t("guestSearchLimit.moreSearch")}
              </Typography>
            </div>
          )}
        </div>

        {/* SUBMIT */}
        <div className="p-2.5">
          <Button
            size="lg"
            type="submit"
            className="w-full shadow-lg text-base"
            disabled={isSearchDisabled}
          >
            <SearchIcon /> {t("searchMedia")}{isGuest && t("guestSearchLimit.searchCount", { count: remainingCount })}
          </Button>
        </div>
      </form>
    </Form>
  );
}
